import { useState } from 'react';
import { DocumentSubtitleSegmentV1 } from '@utils/types/editors/subtitles/subtitle.ts';
import ScriptixCaptionEditor from '../scriptix-caption-editor.tsx';
import { useThemeContext } from '@utils/providers/theme-context.tsx';
import { useThemeVars } from '@utils/hooks/use-theme-vars.ts';
import cn from 'classnames';

type Props = {
  segment: DocumentSubtitleSegmentV1;
  onWordOutOfView: (
    ref: HTMLSpanElement | null,
    direction: 'up' | 'down'
  ) => void;
  onWordInView: () => void;
  index: number;
  filteredItems: DocumentSubtitleSegmentV1[];
  readOnly?: boolean;
  /**
   * `sourceSegment` is the source's paired row (index-matched) when
   * this row renders inside the translation pane of the split view.
   *
   * #2291 reversed #2250's "translation timings are read-only" rule.
   * Translations now own their own start/stop and are freely editable
   * from the translation pane. The source and translation panes stay
   * aligned by segment index, not by timing — so timestamps may drift
   * between the two documents and that is intentional.
   *
   * The one thing `sourceSegment` still controls is the count-tools
   * cluster (add / split / kebab). Those remain hidden on the
   * translation side because adding or splitting there would drift
   * the segment count and break the index pairing with the source.
   *
   * Undefined = single-editor mode or the source pane itself.
   */
  sourceSegment?: DocumentSubtitleSegmentV1;
};

export const CaptionSegment = ({
  index,
  segment,
  onWordOutOfView,
  onWordInView,
  filteredItems,
  readOnly,
  sourceSegment
}: Props) => {
  const { theme } = useThemeContext();
  const { vars, cls } = useThemeVars();
  const [isHovered, setIsHovered] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  // Theme-aware colors for hover/focus states
  const pick = (light: string, dark: string) =>
    theme === 'dark' ? dark : light;

  const activeBorderColor = pick('rgb(191 219 254)', 'rgb(59 130 246)'); // blue-200 / blue-500
  const activeBgColor = pick('rgb(239 246 255)', 'rgba(30, 58, 138, 0.2)'); // blue-50 / blue-900/20
  const hoverBorderColor = pick('rgb(229 231 235)', 'rgb(55 65 81)'); // gray-200 / gray-700
  const hoverBgColor = pick('rgb(249 250 251)', 'rgba(31, 41, 55, 0.3)'); // gray-50 / gray-800/30

  const getStyles = () => {
    if (isFocused) {
      return {
        borderColor: activeBorderColor,
        backgroundColor: activeBgColor
      };
    }
    if (isHovered) {
      return {
        borderColor: hoverBorderColor,
        backgroundColor: hoverBgColor
      };
    }
    // Default: use theme vars (secondLayer background, theme border)
    return {};
  };

  if (readOnly) {
    // Split-view source pane: visual-only. pointer-events + user-select
    // are killed so clicks/hover/text-selection pass through; the outer
    // Virtuoso scroller keeps pointer-events so wheel scrolling still
    // works. Editable's tabIndex is -1 so focus can't land inside.
    return (
      <div
        className={cn(
          'flex border border-t-0 pointer-events-none select-none',
          cls.border,
          'bg-[var(--card-bg)]'
        )}
        style={{
          ...vars,
          fontStyle: 'normal',
          fontWeight: 'normal',
          textDecoration: 'none',
          cursor: 'default'
        }}
        aria-readonly="true"
      >
        <div className="w-full">
          <ScriptixCaptionEditor
            index={index}
            segment={segment}
            onWordOutOfView={onWordOutOfView}
            onWordInView={onWordInView}
            filteredItems={filteredItems}
            readOnly
            sourceSegment={sourceSegment}
          />
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'flex transition-all duration-200 border border-t-0',
        // Default state uses theme classes
        !isHovered && !isFocused && cls.border,
        !isHovered && !isFocused && 'bg-[var(--card-bg)]'
      )}
      style={{
        ...vars,
        ...getStyles(),
        // Reset font styles to prevent CSS inheritance from affecting segment content
        fontStyle: 'normal',
        fontWeight: 'normal',
        textDecoration: 'none'
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocusCapture={() => setIsFocused(true)}
      onBlurCapture={() => setIsFocused(false)}
    >
      <div className="w-full">
        <ScriptixCaptionEditor
          index={index}
          segment={segment}
          onWordOutOfView={onWordOutOfView}
          onWordInView={onWordInView}
          filteredItems={filteredItems}
          sourceSegment={sourceSegment}
        />
      </div>
    </div>
  );
};

export default CaptionSegment;
